// Tenant-gated content loader.
//
// Some legacy content is confidential to a single tenant (a manufacturer's
// form packet, its contract bodies, its BAA). It cannot ship in the shared
// entry bundle, because that bundle is downloaded byte-for-byte by that
// tenant's direct competitors on the same frontend build.
//
// Everything gated here is loaded with a dynamic `import()`, so webpack
// emits it as a separate chunk that the app requests only after the
// session's brand resolves to that tenant.
//
// THIS IS ROUTING AND BUNDLE HYGIENE, NOT ACCESS CONTROL. Emitted chunks
// are static assets served without a session, and the brand signal below
// comes from client-writable browser storage. Anyone who reads the webpack
// runtime can fetch any chunk directly. What the gate buys is that one
// tenant's content is not in the other's eager bundle and is never rendered
// in the other's session — worth having, but the server remains the only
// authority. ADMISSION RULE: nothing may be placed behind this gate that
// would be materially harmful to disclose to a competitor or to an
// anonymous visitor. Bank credentials and remittance details never qualify.
//
// New content should NOT be added here. The durable home for per-tenant
// documents is the versioned template API (`services/templateApi`), which
// scopes every read to the caller's organization server-side. This module
// exists for the one packet that predates that API.

import { detectBrandFromBrainUrl, isKnownBrand } from "../utils/brand";

// Brand of the tenant this session is bound to.
//
// `sessionStorage.brand` is set at login from the hostname / invite link.
// `localStorage.hostName` is the authenticated brain URL the session is
// actually talking to, so it is the stronger signal when both disagree —
// but it is only present after login, hence the ordering.
export function resolveTenantBrand() {
  try {
    const fromBrain = detectBrandFromBrainUrl(localStorage.getItem("hostName"));
    if (fromBrain) return fromBrain;
    const stored = typeof sessionStorage !== "undefined" ? sessionStorage.getItem("brand") : null;
    if (isKnownBrand(stored)) return stored.toLowerCase();
  } catch (_e) {
    // Storage can throw in locked-down/private browsing contexts. No brand
    // resolved means no gated content — the safe direction.
  }
  return null;
}

// brand -> () => Promise<module>. Keys are the ONLY brands that may receive
// the corresponding chunk.
const NAF_PACKET_LOADERS = {
  gtm: () => import(/* webpackChunkName: "tenant-naf-gtm" */ "./packs/gtm/legacyNafPacket"),
};

const packetCache = new Map();

// Resolve the calling tenant's legacy NAF packet.
//
// Returns null — never another tenant's packet, and never a shared default —
// when the session's brand has no bundled packet. Callers must treat null as
// "this org has no form configured" and render a neutral empty state.
export function loadTenantNafPacket(brandOverride) {
  const brand = brandOverride ?? resolveTenantBrand();
  const loader = brand && Object.prototype.hasOwnProperty.call(NAF_PACKET_LOADERS, brand) ? NAF_PACKET_LOADERS[brand] : null;
  if (!loader) return Promise.resolve(null);
  if (!packetCache.has(brand)) {
    packetCache.set(
      brand,
      loader().then(
        (mod) => mod.default ?? mod,
        () => null
      )
    );
  }
  return packetCache.get(brand);
}

// ---------------------------------------------------------------------------
// BAA bodies
// ---------------------------------------------------------------------------
//
// Each manufacturer's Business Associate Agreement is its own document.
// Statically importing all of them put every manufacturer's BAA body into
// the entry bundle, so every session downloaded every variant. Each is now
// its own chunk, requested only when a session actually renders that
// variant. Per the admission rule above these hold legal prose only.
//
// Which variants a session may render is decided upstream by
// useAuth.availableBaas, from the org's own `orderManufacturer`.
const BAA_LOADERS = {
  lucas: () => import(/* webpackChunkName: "baa-lucas" */ "../components/termsOfUse/BaaAgreementLucas"),
  shoreline: () => import(/* webpackChunkName: "baa-shoreline" */ "../components/termsOfUse/BaaAgreementShoreline"),
  meridian: () => import(/* webpackChunkName: "baa-meridian" */ "../components/termsOfUse/BaaAgreementMeridian"),
  gtm: () => import(/* webpackChunkName: "baa-gtm" */ "../components/termsOfUse/BaaAgreementGTM"),
  optimalcaregrp: () => import(/* webpackChunkName: "baa-optimalcaregrp" */ "../components/termsOfUse/BaaAgreementOptimalCare"),
};

export const BAA_VARIANT_IDS = Object.keys(BAA_LOADERS);

// Raw chunk loader for a BAA variant, or null when the variant is unknown.
export function baaChunkLoader(variant) {
  return Object.prototype.hasOwnProperty.call(BAA_LOADERS, variant) ? BAA_LOADERS[variant] : null;
}

// Non-React accessor: resolves to the variant's component, or null.
export function loadBaaComponent(variant) {
  const loader = baaChunkLoader(variant);
  if (!loader) return Promise.resolve(null);
  return loader().then(
    (mod) => mod?.default ?? null,
    () => null
  );
}

// Test seam — the chunk cache is module-global and would otherwise leak
// between cases that switch brands.
export function __resetTenantContentCache() {
  packetCache.clear();
}
